/**
 * Drop-in MUI TextField with workflow expression autocomplete: every
 * TextField prop is forwarded; this only adds caret-tracking, highlighting
 * and the suggestion popup. Field validation stays the caller's job.
 */

import { FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import ExpressionAutocompletePopper from './ExpressionAutocompletePopper'
import ExpressionHighlightOverlay, { INPUT_TEXT_COLOR } from './ExpressionHighlightOverlay'
import {
  ActiveFieldApi,
  useActiveExpressionField,
  useAutocompleteContext,
} from './ExpressionContext'
import {
  AutocompleteItem,
  useExpressionAutocomplete,
} from './useExpressionAutocomplete'
import { useExpressionValidation } from './useExpressionValidation'
import type { ValidationIssue } from './validateExpression'

const formatValidationMessage = (
  translate: (key: string, options?: Record<string, unknown>) => string,
  issue: ValidationIssue,
): string => translate(issue.messageKey, issue.messageArgs ?? {})

export interface ExpressionTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: string
  onChange: (event: { target: { value: string } }) => void
}

const ExpressionTextField: FC<ExpressionTextFieldProps> = ({
  value,
  onChange,
  inputRef: externalInputRef,
  inputProps,
  InputProps: callerInputProps,
  error: errorProp,
  helperText: helperTextProp,
  onKeyDown: callerOnKeyDown,
  ...rest
}) => {
  const translate = useTranslate()
  const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  // State mirror of internalRef so the highlight overlay re-renders once the element exists.
  const [inputEl, setInputEl] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const [caret, setCaret] = useState<number>(0)
  const [highlighted, setHighlighted] = useState<number>(0)
  // Popup is gated on focus so a click anywhere else dismisses it.
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const ctx = useAutocompleteContext()
  const activeRegistry = useActiveExpressionField()
  const state = useExpressionAutocomplete(value, caret, ctx)
  const validation = useExpressionValidation(value, caret, isFocused)

  // onChange lives in a ref so the stable insertAtCaret closure (used by the
  // input-data panel) always commits through the latest caller handler.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const insertAtCaret = useCallback((text: string, caretOffset?: number) => {
    const el = internalRef.current
    if (!el) return
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? start
    const next = el.value.slice(0, start) + text + el.value.slice(end)
    onChangeRef.current({ target: { value: next } })
    const nextCaret = start + (caretOffset ?? text.length)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(nextCaret, nextCaret)
      setCaret(nextCaret)
    })
  }, [])

  const fieldApiRef = useRef<ActiveFieldApi>({ insertAtCaret })

  useEffect(() => {
    const api = fieldApiRef.current
    return () => activeRegistry.clearActiveField(api)
  }, [activeRegistry])

  // A caller-provided error wins, so per-field validation stays composable.
  const ownError = errorProp ? null : validation.visibleError
  const errorMessage = ownError ? formatValidationMessage(translate, ownError) : null
  const showValidationError = ownError !== null

  // Clamp the highlight without touching state during render.
  const effectiveHighlighted =
    state.items.length > 0 ? Math.min(highlighted, state.items.length - 1) : 0

  const setRef = (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    internalRef.current = el
    setInputEl(el)
    if (typeof externalInputRef === 'function') externalInputRef(el)
    else if (externalInputRef && 'current' in externalInputRef) {
      ;(externalInputRef as React.MutableRefObject<unknown>).current = el
    }
  }

  // With an expression present the input's own text turns transparent (caret
  // stays visible) and the overlay paints it with the {{ … }} pills.
  const hasExpression = value.includes('{{')
  const highlightStyle = hasExpression
    ? { color: 'transparent', caretColor: INPUT_TEXT_COLOR }
    : undefined

  const updateCaret = () => {
    const el = internalRef.current
    if (el) setCaret(el.selectionStart ?? 0)
  }

  const applyItem = (item: AutocompleteItem) => {
    const { value: next, caret: nextCaret } = state.apply(item)
    onChange({ target: { value: next } })
    // Move the DOM caret after React applies the new value.
    requestAnimationFrame(() => {
      const el = internalRef.current
      if (el) {
        el.focus()
        el.setSelectionRange(nextCaret, nextCaret)
        setCaret(nextCaret)
      }
    })
  }

  // Returns true when the autocomplete popup consumed the key.
  const handlePopupKey = (e: KeyboardEvent<HTMLDivElement>): boolean => {
    if (!state.open) return false
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((i) => (i + 1) % state.items.length)
      return true
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((i) => (i - 1 + state.items.length) % state.items.length)
      return true
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      // eslint-disable-next-line security/detect-object-injection
      const item = state.items[highlighted]
      if (item) {
        e.preventDefault()
        applyItem(item)
        return true
      }
      return false
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setHighlighted(0)
      return true
    }
    return false
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!handlePopupKey(e)) callerOnKeyDown?.(e)
  }

  return (
    <>
      <TextField
        {...rest}
        value={value}
        error={errorProp || showValidationError}
        // A live validation issue outranks the caller's static helper text.
        helperText={showValidationError ? errorMessage ?? undefined : helperTextProp}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onKeyUp={updateCaret}
        onClick={updateCaret}
        onFocus={() => {
          setIsFocused(true)
          updateCaret()
          activeRegistry.setActiveField(fieldApiRef.current)
        }}
        onBlur={() => setIsFocused(false)}
        inputProps={{ ...inputProps, style: { ...inputProps?.style, ...highlightStyle } }}
        InputProps={callerInputProps}
        inputRef={setRef}
      />
      {hasExpression && inputEl && (
        <ExpressionHighlightOverlay inputEl={inputEl} value={value} />
      )}
      <ExpressionAutocompletePopper
        anchorEl={internalRef.current as HTMLElement | null}
        open={(state.open || state.functionsLoadFailed) && isFocused && state.span !== null}
        items={state.items}
        highlightedIndex={effectiveHighlighted}
        functionsLoadFailed={state.functionsLoadFailed}
        onHover={setHighlighted}
        onSelect={applyItem}
      />
    </>
  )
}

export default ExpressionTextField
